import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../AuthContext';
import { Loader2, ShieldOff, Shield, AlertTriangle } from 'lucide-react';

// Componente para una sola tarjeta de producto (Sin cambios)
const ProductCard = ({ product, isRestricted, onToggle }) => {
    const [loading, setLoading] = useState(false);

    const handleToggle = async () => {
        setLoading(true);
        await onToggle(!isRestricted);
        setLoading(false);
    };

    return (
        <div className={`border rounded-lg p-3 flex flex-col items-center relative transition-all duration-300 ${isRestricted ? 'bg-red-50' : 'bg-white'}`}>
            {loading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-lg"><Loader2 className="animate-spin text-gray-400" /></div>}
            
            <img 
                src={product.image_url || 'https://placehold.co/150x150/f3f4f6/9ca3af?text=Sin+Imagen'}
                alt={product.name}
                className="w-28 h-28 object-cover rounded-md mb-3"
            />
            <p className="text-center text-sm font-medium text-gray-700 h-10">{product.name}</p>
            
            <button 
                onClick={handleToggle}
                className={`w-full mt-3 py-2 text-sm font-semibold rounded-md flex items-center justify-center gap-2 transition-colors ${
                    isRestricted 
                    ? 'bg-red-500 text-white hover:bg-red-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
                {isRestricted ? <ShieldOff size={16} /> : <Shield size={16} />}
                {isRestricted ? 'Restringido' : 'Permitido'}
            </button>
        </div>
    );
};

// Componente principal de la página de restricciones
const Restricciones = () => {
    const { userData, userType, loading: authLoading } = useAuth();
    const [products, setProducts] = useState({});
    const [restrictions, setRestrictions] = useState(new Set());
    const [loading, setLoading] = useState(true);

    // Carga los productos y las restricciones para el alumno que ha iniciado sesión
    const fetchDataForStudent = useCallback(async () => {
        // Solo se ejecuta si el usuario es un estudiante y sus datos están cargados
        if (userType !== 'student' || !userData?.id || !userData?.school_id) {
            setLoading(false);
            return;
        }
        
        setLoading(true);
        const { id: studentId, school_id: schoolId } = userData;

        try {
            const [productRes, restrictionRes] = await Promise.all([
                supabase.from('products').select('*').eq('school_id', schoolId).eq('is_active', true),
                supabase.from('student_product_restrictions').select('product_id').eq('student_id', studentId)
            ]);

            if (productRes.error) throw productRes.error;
            if (restrictionRes.error) throw restrictionRes.error;

            // --- INICIO DE MODIFICACIÓN ---

            // 1. Definimos las categorías a excluir, en minúsculas.
            const forbiddenCategories = ['almuerzos', 'desayunos'];

            // 2. Filtramos el array de productos ANTES de agrupar
            const filteredProducts = (productRes.data || []).filter(p => {
                // Obtenemos el nombre de la categoría (o 'otros' si es nula)
                // y la convertimos a minúsculas para comparar.
                const categoryName = (p.category || 'otros').toLowerCase();
                
                // Devolvemos 'true' solo si la categoría NO está en la lista prohibida
                return !forbiddenCategories.includes(categoryName);
            });
            
            // 3. Agrupa los productos (ya filtrados) por categoría
            const groupedProducts = filteredProducts.reduce((acc, p) => {
                // Corregimos el fallback para que sea solo 'Otros'
                const category = p.category || 'Otros';
                if (!acc[category]) acc[category] = [];
                acc[category].push(p);
                return acc;
            }, {});
            
            // --- FIN DE MODIFICACIÓN ---

            setProducts(groupedProducts);

            // Guarda las restricciones en un Set para búsquedas rápidas
            const restrictionSet = new Set((restrictionRes.data || []).map(r => r.product_id));
            setRestrictions(restrictionSet);

        } catch (error) {
            console.error("Error al cargar datos de restricciones:", error);
        } finally {
            setLoading(false);
        }
    }, [userData, userType]);

    useEffect(() => {
        // Se asegura de que los datos de autenticación se hayan cargado antes de buscar
        if (!authLoading) {
            fetchDataForStudent();
        }
    }, [authLoading, fetchDataForStudent]);

    // Función para activar/desactivar una restricción (Sin cambios)
    const handleToggleRestriction = async (productId, shouldRestrict) => {
        const studentId = userData?.id;
        if (!studentId) return;

        if (shouldRestrict) {
            // Añade una nueva restricción
            const { error } = await supabase.from('student_product_restrictions').insert({
                student_id: studentId,
                product_id: productId,
            });
            if (error) {
                console.error("Error al añadir restricción:", error);
            } else {
                setRestrictions(prev => new Set(prev).add(productId));
            }
        } else {
            // Elimina una restricción existente
            const { error } = await supabase.from('student_product_restrictions')
                .delete()
                .match({ student_id: studentId, product_id: productId });
            if (error) {
                console.error("Error al quitar restricción:", error);
            } else {
                setRestrictions(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(productId);
                    return newSet;
                });
            }
        }
    };
    
    // Muestra un loader mientras se cargan los datos del usuario (Sin cambios)
    if (authLoading) {
        return <div className="flex justify-center items-center py-20"><Loader2 className="animate-spin text-orange-500" size={48} /></div>;
    }
    
    // Muestra un mensaje si el usuario NO es un estudiante (Sin cambios)
    if (userType !== 'student') {
        return (
             <div className="p-4 sm:p-6 lg:p-8 text-center">
                <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">Función no disponible</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Esta sección es solo para que los alumnos gestionen sus propias restricciones de productos.
                </p>
            </div>
        );
    }

    // Renderizado (Sin cambios)
    return (
        <div className="bg-gray-50 p-4 sm:p-6 lg:p-8 min-h-full">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Restringir Consumo de Productos</h1>
            
            {loading ? (
                <div className="flex justify-center items-center py-20"><Loader2 className="animate-spin text-orange-500" size={48} /></div>
            ) : Object.keys(products).length === 0 ? (
                 <p className="text-center text-gray-500 py-10">No hay productos disponibles para configurar en tu colegio.</p>
            ) : (
                <div className="space-y-8">
                    {Object.entries(products).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
                        <div key={category}>
                            <h2 className="text-xl font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-orange-200">{category}</h2>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                {items.map(product => (
                                    <ProductCard 
                                        key={product.id}
                                        product={product}
                                        isRestricted={restrictions.has(product.id)}
                                        onToggle={(shouldRestrict) => handleToggleRestriction(product.id, shouldRestrict)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Restricciones;